/**
 * Адаптер очереди ситуаций: `/situations` → форма `Situation` прототипа.
 *
 * Очередь Astra считалась в браузере (`astra/situations.ts: buildSituations`)
 * поверх развёрнутого журнала. На каноне её считает сервер тем же детектором,
 * которым потом проверяет основания при записи диспозиции, — и версия правил
 * с их хэшем приезжают в ответе. Экран очереди не меняется: он получает те же
 * поля, только источник у них другой.
 *
 * Три расхождения семантики закрыты словами, а не подгонкой чисел:
 *
 * 1. Сервер называет ОБЯЗАТЕЛЬСТВА, а не контуры: `contourIds` пуст, площадь
 *    хозяйства и площадь совпадения — `null`. Карта хозяйства в разборе на
 *    каноне поэтому не рисуется, и вместо неё стоит фраза «нет данных».
 * 2. `water_gap` сервера — ЧИСЛО ОБЯЗАТЕЛЬСТВ с незакрытой подачей, а у
 *    прототипа `endedWaterGap` — кубометры. Это разные величины под похожим
 *    именем, поэтому кубометры остаются `null`, а счёт едет своим полем.
 * 3. Заголовок, объяснение и следующий шаг собираются по ВИДУ сигнала
 *    (`kind`) — закрытому словарю сервера (`docs/model/situations-rules.yaml`),
 *    а величины в них подставляются из полей того же ответа. Свободного текста
 *    здесь нет: вид сигнала называет сервер, перевод вида на русский — экран.
 */

import type { QueryParams, Schemas, SituationList } from '../../../api/client';
import type { Situation, SituationLens } from '../../astra/situations';
import type { Scope } from '../../astra/model';
import { format, round } from '../../astra/model';
import { workHaUnit } from '../../lib/areaWork';
import { hectares, measured, seasonQuery } from './season';
import { defineMessages, type Message } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  titleConflict: {
    id: 'data.situations.title.reportConflict',
    defaultMessage: 'Отчёт расходится с проверкой',
    description: 'Заголовок строки очереди внимания: отчёт хозяйства и отметка проверяющего расходятся',
  },
  titleCombined: {
    id: 'data.situations.title.combined',
    defaultMessage: 'Полив: нет отчёта при водной строке',
    description: 'Заголовок строки очереди внимания: по поливу нет отчёта, хотя в водной ведомости строка есть',
  },
  titleWaterGap: {
    id: 'data.situations.title.waterGap',
    defaultMessage: 'Окно закрылось, подача не подтверждена',
    description: 'Заголовок строки очереди внимания: окно полива закрыто, подачу воды никто не подтвердил',
  },
  titleDueSoon: {
    id: 'data.situations.title.dueSoon',
    defaultMessage: 'Окно работы скоро закроется',
    description: 'Заголовок строки очереди внимания: окно работы закроется в ближайшие дни',
  },
  titleOverdue: {
    id: 'data.situations.title.overdue',
    defaultMessage: 'После срока не хватает отчётов',
    description: 'Заголовок строки очереди внимания: срок прошёл, отчётов нет',
  },
  titleVerification: {
    id: 'data.situations.title.verification',
    defaultMessage: 'Отчёты без отметки проверяющего',
    description: 'Заголовок строки очереди внимания: отчёты приняты, проверяющий их не отметил',
  },
  titleQuarantine: {
    id: 'data.situations.title.quarantine',
    defaultMessage: 'Карантин: контуры в красном или жёлтом',
    description: 'Заголовок строки очереди внимания: на контурах хозяйства красный или жёлтый карантинный статус',
  },
  stepConflict: {
    id: 'data.situations.step.reportConflict',
    defaultMessage: 'Сопоставить первичный отчёт и замечание проверяющего. Зафиксировать уточнение и повторную проверку.',
    description: 'Что делать дальше (в строке очереди внимания, для диспетчера): отчёт и проверка расходятся',
  },
  stepCombined: {
    id: 'data.situations.step.combined',
    defaultMessage: 'Запросить у хозяйства отчёт по этим работам, у водной службы — подтверждение подачи из её собственного журнала. Сейчас подтверждения нет ни с одной стороны.',
    description: 'Что делать дальше (в строке очереди внимания): по поливу нет отчёта хозяйства и нет подтверждения водной службы',
  },
  stepWaterGap: {
    id: 'data.situations.step.waterGap',
    defaultMessage: 'Запросить у водной службы подтверждение подачи из её журнала: окно работы закрылось, а заявка осталась незакрытой.',
    description: 'Что делать дальше (в строке очереди внимания): окно закрыто, заявка на воду не закрыта',
  },
  stepDueSoon: {
    id: 'data.situations.step.dueSoon',
    defaultMessage: 'Уточнить у ответственного ожидаемую дату отчёта и наличие препятствий до окончания окна.',
    description: 'Что делать дальше (в строке очереди внимания): окно работы скоро закроется',
  },
  stepOverdue: {
    id: 'data.situations.step.overdue',
    defaultMessage: 'Связаться с хозяйством, уточнить выполнение и получить отчёт с датой и автором.',
    description: 'Что делать дальше (в строке очереди внимания): срок прошёл, отчёта нет',
  },
  stepVerification: {
    id: 'data.situations.step.verification',
    defaultMessage: 'Действий не требуется: отчёт принят как факт, проверка — по желанию проверяющего.',
    description: 'Что делать дальше (в строке очереди внимания): отчёты приняты, отметка проверяющего необязательна',
  },
  stepQuarantine: {
    id: 'data.situations.step.quarantine',
    defaultMessage: 'Проверить исполнение предписаний на красных и жёлтых контурах и срок повторного обследования.',
    description: 'Что делать дальше (в строке очереди внимания): красные и жёлтые карантинные контуры',
  },
  explainConflict: {
    id: 'data.situations.explain.reportConflict',
    defaultMessage: '{count, plural, one {{shown} отчёт опровергнут} few {{shown} отчёта опровергнуты} many {{shown} отчётов опровергнуто} other {{shown} отчёта опровергнуто}}. Нужно уточнить основание расхождения.',
    description: 'Объяснение строки очереди внимания: число отчётов, которые проверяющий опроверг; {count} — число (для склонения), {shown} — число или «—», когда сервер не назвал',
  },
  explainCombined: {
    id: 'data.situations.explain.combined',
    defaultMessage: '{count, plural, one {{shown} полив} few {{shown} полива} many {{shown} поливов} other {{shown} полива}} без подачи: окно полива на контуре закрылось, а события в файле ирригации нет.',
    description: 'Объяснение строки очереди внимания: {count} — число работ полива (для склонения), {shown} — число или «—»',
  },
  explainWaterGap: {
    id: 'data.situations.explain.waterGap',
    defaultMessage: '{count, plural, one {{shown} полив} few {{shown} полива} many {{shown} поливов} other {{shown} полива}} без подачи: окно полива на контуре закрылось, а события в файле ирригации нет.',
    description: 'Объяснение строки очереди внимания: {count} — число работ на полях (для склонения), {shown} — число или «—»',
  },
  explainDueSoon: {
    id: 'data.situations.explain.dueSoon',
    defaultMessage: '{count, plural, one {{shown} работа на поле} few {{shown} работы на полях} many {{shown} работ на полях} other {{shown} работы на полях}} без отчёта в окнах, до конца которых осталось немного. Окно ещё открыто.',
    description: 'Объяснение строки очереди внимания: {count} — число работ на полях (для склонения), {shown} — число или «—»',
  },
  explainOverdue: {
    id: 'data.situations.explain.overdue',
    defaultMessage: '{count, plural, one {{shown} работа на поле} few {{shown} работы на полях} many {{shown} работ на полях} other {{shown} работы на полях}} без отчёта после окончания окна{area}. Фактическое выполнение требует уточнения.',
    description: 'Объяснение строки очереди внимания: {count}/{shown} — число работ на полях; {area} — пусто или « (1 234 га работ)» (в скобках, с пробелом в начале)',
  },
  explainVerification: {
    id: 'data.situations.explain.verification',
    defaultMessage: '{count, plural, one {{shown} отчёт} few {{shown} отчёта} many {{shown} отчётов} other {{shown} отчёта}} без отметки проверяющего; они приняты как факт.',
    description: 'Объяснение строки очереди внимания: {count}/{shown} — число отчётов без отметки проверяющего',
  },
  explainQuarantine: {
    id: 'data.situations.explain.quarantine',
    defaultMessage: '{flagged} из {total} контуров хозяйства в красном или жёлтом статусе либо с предписанием после срока.',
    description: 'Объяснение строки очереди внимания по карантину; {flagged}/{total} — числа контуров или «—»',
  },
  farmNo: {
    id: 'data.situations.farmNo',
    defaultMessage: 'Хозяйство №{id}',
    description: 'Название хозяйства в очереди внимания, когда сервер имя не назвал; {id} — номер',
  },
});


/**
 * Срез прототипа → параметры очереди.
 *
 * Очередь принимает ось СЕЗОНА, а не ключ цикла: ситуация — это состояние
 * хозяйства на дату, а не строка внутри одной культуры. Послать сюда `cycle`
 * значило бы отправить параметр, которого выдача не читает, и молча получить
 * неотфильтрованную очередь под видом отфильтрованной.
 */
export function situationQuery(scope: Scope, extra: QueryParams = {}): QueryParams {
  const { cycle, ...rest } = seasonQuery(scope);
  const season = typeof cycle === 'string' ? cycle.split(':')[0] : null;
  return { ...rest, ...(season ? { season } : {}), ...extra };
}

/**
 * Контракт потока 6al.2 (карантин в очереди) — заглушка поверх
 * сгенерированных типов, пока `api/generated.ts` не перегенерирован:
 * вид `quarantine`, линза `quarantine`, поля `quarantine` и `contours_total`
 * у строки. Все добавки необязательны: старый сервер их не шлёт, и экран
 * обязан это пережить (вкладка «Карантин» скрыта, значок не рисуется).
 */
export type SituationKind = Schemas['SituationKind'] | 'quarantine';
export type SituationLensV2 = Schemas['SituationLens'] | 'quarantine';
export type SituationRow = Omit<Schemas['SituationOut'], 'kind' | 'lenses'> & {
  kind: SituationKind;
  lenses: SituationLensV2[];
  /** Контуров с карантинным сигналом (6al.2). */
  quarantine?: number;
  /** Знаменатель карантина: все контуры землепользования хозяйства (6al.2). */
  contours_total?: number;
};

/** Перевод вида сигнала: заголовок строки очереди. */
const TITLE: Record<SituationKind, Message> = {
  report_conflict: m.titleConflict,
  combined_overdue_and_water: m.titleCombined,
  water_gap_after_window: m.titleWaterGap,
  window_due_soon: m.titleDueSoon,
  overdue_material: m.titleOverdue,
  verification_pending: m.titleVerification,
  quarantine: m.titleQuarantine,
};

/** Перевод вида сигнала: что делать дальше. Действие, а не оценка. */
const NEXT_STEP: Record<SituationKind, Message> = {
  report_conflict: m.stepConflict,
  combined_overdue_and_water: m.stepCombined,
  water_gap_after_window: m.stepWaterGap,
  window_due_soon: m.stepDueSoon,
  overdue_material: m.stepOverdue,
  verification_pending: m.stepVerification,
  quarantine: m.stepQuarantine,
};

/**
 * Объяснение строки. Числа берутся из полей ответа — второй копии величины
 * внутри фразы не заводится, поэтому расходиться нечему.
 */
/**
 * Число со словом в нужном падеже: «1 отчёт опровергнут», «3 отчёта
 * опровергнуты», «5 отчётов опровергнуто» (agroai-6al.42). Не измерено —
 * «—» с формой множества: падеж прочерка не выбирают.
 */
function counted(n: number | null): { count: number; shown: string } {
  return { count: n ?? 0, shown: format(n) };
}

function explain(row: SituationRow): string {
  const area = hectares(measured(row.overdue_area));
  switch (row.kind) {
    case 'report_conflict':
      return tr(m.explainConflict, counted(row.conflicts));
    case 'combined_overdue_and_water':
      return tr(m.explainCombined, counted(row.combined));
    case 'water_gap_after_window':
      return tr(m.explainWaterGap, counted(row.water_gap));
    case 'window_due_soon':
      return tr(m.explainDueSoon, counted(row.due_soon));
    case 'overdue_material':
      return tr(m.explainOverdue, {
        ...counted(row.overdue),
        area: area === null ? '' : ` (${format(area, 1)} ${workHaUnit()})`,
      });
    case 'verification_pending':
      return tr(m.explainVerification, counted(row.awaiting));
    case 'quarantine':
      return tr(m.explainQuarantine, { flagged: format(row.quarantine ?? null), total: format(row.contours_total ?? null) });
  }
}

export function toSituation(row: SituationRow): Situation {
  const works = row.works.map((work) => ({
    id: String(work.work_def_id),
    name: work.name,
    signals: work.count,
  }));
  return {
    key: row.key,
    kind: row.kind,
    farmId: String(row.actor.id),
    name: row.actor.name ?? tr(m.farmNo, { id: row.actor.id }),
    // Контуров сервер не называет: основания — обязательства и записи проверки.
    contourIds: [],
    area: null,
    total: row.total,
    reported: row.reported,
    verified: row.verified,
    conflicts: row.conflicts,
    overdue: row.overdue,
    overdueArea: hectares(measured(row.overdue_area)),
    dueSoon: row.due_soon,
    // Заявка и подача по хозяйству едут «картиной сезона», а не очередью.
    requested: null,
    delivered: null,
    endedWaterGap: null,
    waterGapObligations: row.water_gap,
    awaiting: row.awaiting,
    overlapContours: null,
    overlapObligations: row.combined,
    overlapArea: null,
    workId: works[0]?.id ?? '',
    workName: works[0]?.name ?? '',
    relatedWorks: works,
    nextDeadline: row.next_deadline,
    title: tr(TITLE[row.kind]),
    explanation: explain(row),
    nextStep: tr(NEXT_STEP[row.kind]),
    lenses: row.lenses as SituationLens[],
    disposition: row.disposition
      ? { status: row.disposition.status, comment: row.disposition.comment }
      : null,
    waterIsDerived: row.water_is_derived,
    quarantine: typeof row.quarantine === 'number' ? row.quarantine : null,
    contoursTotal: typeof row.contours_total === 'number' ? row.contours_total : null,
    dueUndoneHa: typeof row.due_undone_microha === 'number' ? row.due_undone_microha / 1_000_000 : null,
    overdueWorkHa: typeof row.overdue_work_microha === 'number' ? row.overdue_work_microha / 1_000_000 : null,
  };
}

export interface SituationsView {
  items: Situation[];
  /**
   * Счётчики соседних линз — выбор следующей линзы не должен быть вслепую.
   * `quarantine` есть, только если сервер его назвал (6al.2).
   */
  byLens: Record<Exclude<SituationLens, 'quarantine' | 'any' | 'deadlines'>, number> & {
    quarantine?: number;
    /** Объединение категорий Обзора (agroai-6al.35); нет — сервер не назвал. */
    any?: number;
    /** «Сроки»: после срока или окно скоро закроется (agroai-6al.35). */
    deadlines?: number;
  };
  /** Сколько строк всего в этой линзе; список приезжает страницей. */
  total: number;
  /**
   * Разбор очереди линзы по отметкам человека — по ВСЕЙ очереди, а не по
   * загруженной странице (agroai-b48.3). `null` — сервер итогов не прислал
   * (старый ответ): тогда остаётся счёт по загруженным строкам.
   */
  queueTally: { fresh: number; inWork: number; closed: number } | null;
  /** Оговорки — словарные статьи ответа (`template_id` → текст). */
  notes: string[];
  /** Что означает пустая очередь. Пустой список ≠ «всё проверено». */
  emptyNote: string;
  rulesVersion: string;
  materiality: { obligations: number; areaHa: number; dueSoonDays: number };
}

export function toSituationsView(list: SituationList): SituationsView {
  const byLens: SituationsView['byLens'] = { all: 0, combined: 0, water: 0, verification: 0, window: 0, quarantine: 0 };
  for (const row of list.by_lens) byLens[row.lens] = row.count;
  const notes = [list.coincidence_note, list.materiality_note, list.water_note]
    .filter((note): note is Schemas['SituationNoteOut'] => note !== null)
    .map((note) => note.text);
  return {
    items: (list.items as SituationRow[]).map(toSituation),
    byLens,
    total: list.page.total,
    queueTally: list.summary
      ? { fresh: list.summary.fresh, inWork: list.summary.in_work, closed: list.summary.closed }
      : null,
    notes,
    emptyNote: list.empty_note,
    rulesVersion: list.rules_version,
    materiality: {
      obligations: list.materiality.obligations,
      areaHa: round(list.materiality.area_ha),
      dueSoonDays: list.materiality.due_soon_days,
    },
  };
}
